import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Save, TrendingUp, Users2, Heart, Download, Music2 } from "lucide-react";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { CityPicker } from "../components/CityPicker";

const GENRES = ["Afrobeat", "Rap", "Hira Gasy", "Gospel", "Salegy", "R&B", "Jazz", "Coupé-Décalé", "Zouk", "Reggae", "Soul", "Autre"];
const COUNTRIES = ["Madagascar", "Sénégal", "Nigeria", "Côte d'Ivoire", "Cameroun", "RDC", "Mali", "Afrique du Sud", "Kenya", "Ghana", "Maroc", "Tunisie"];

export const DashboardPage = () => {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", bio: "", country: "Madagascar", city: "", genre: "Afrobeat", picture: "", youtube: "", spotify: "", instagram: "", tiktok: "", email_opt_in: true });
  const [tracks, setTracks] = useState([]);
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState(null);
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [uploadingPic, setUploadingPic] = useState(false);
  const pictureFileRef = useRef(null);

  const uploadProfilePicture = async (file) => {
    if (file.size > 5 * 1024 * 1024) { toast.error("Image trop lourde (>5MB)"); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", "profile");
    setUploadingPic(true);
    try {
      const { data } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, picture: data.url }));
      toast.success("Photo uploadée ! Clique sur Enregistrer pour la sauvegarder.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload échoué");
    } finally { setUploadingPic(false); }
  };

  useEffect(() => {
    if (!user) { navigate("/login"); return; }
    const links = user.social_links || {};
    setForm({
      name: user.name || "", bio: user.bio || "", country: user.country || "Madagascar",
      city: user.city || "",
      genre: user.genre || "Afrobeat", picture: user.picture || "",
      youtube: links.youtube || "", spotify: links.spotify || "",
      instagram: links.instagram || "", tiktok: links.tiktok || "",
      email_opt_in: user.email_opt_in !== false,
    });
    api.get("/tracks", { params: { user_id: user.user_id } }).then(({ data }) => setTracks(data));
    api.get("/me/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/email/status").then(({ data }) => setEmailEnabled(data.configured)).catch(() => {});
  }, [user, navigate]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/users/me", {
        name: form.name, bio: form.bio, country: form.country, city: form.city, genre: form.genre, picture: form.picture,
        email_opt_in: form.email_opt_in,
        social_links: { youtube: form.youtube, spotify: form.spotify, instagram: form.instagram, tiktok: form.tiktok },
      });
      await refresh();
      toast.success("Profil mis à jour !");
    } catch { toast.error("Erreur"); } finally { setSaving(false); }
  };

  const deleteTrack = async (id) => {
    if (!window.confirm("Supprimer ce morceau ?")) return;
    await api.delete(`/tracks/${id}`);
    setTracks(tracks.filter((t) => t.track_id !== id));
    toast.success("Morceau supprimé");
  };

  if (!user) return null;

  return (
    <div className="px-4 md:px-12 py-10 max-w-6xl mx-auto pb-32" data-testid="dashboard-page">
      <p className="text-[10px] tracking-[0.25em] uppercase text-brand-orange font-bold mb-2">Mon espace</p>
      <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mb-10">Dashboard</h1>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <StatCard label="Morceaux" value={stats.total_tracks} icon={<Music2 size={16} />} />
          <StatCard label="Écoutes" value={stats.total_plays} icon={<TrendingUp size={16} />} accent />
          <StatCard label="Likes" value={stats.total_likes} icon={<Heart size={16} />} />
          <StatCard label="Followers" value={stats.followers} icon={<Users2 size={16} />} accent />
        </div>
      )}

      {stats?.daily_plays?.length > 0 && (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 mb-8" data-testid="dashboard-chart-daily">
          <h3 className="font-display text-lg font-bold mb-4 inline-flex items-center gap-2"><TrendingUp size={16} /> Écoutes (30 derniers jours)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={stats.daily_plays}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="date" stroke="rgba(255,255,255,0.4)" fontSize={10} />
              <YAxis stroke="rgba(255,255,255,0.4)" fontSize={10} />
              <Tooltip contentStyle={{ background: "#1A1514", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }} />
              <Line type="monotone" dataKey="plays" stroke="#F95738" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {stats?.by_country?.length > 0 && (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 mb-10" data-testid="dashboard-chart-country">
          <h3 className="font-display text-lg font-bold mb-4">Écoutes par pays</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={stats.by_country.slice(0, 8)}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="country" stroke="rgba(255,255,255,0.4)" fontSize={10} />
              <YAxis stroke="rgba(255,255,255,0.4)" fontSize={10} />
              <Tooltip contentStyle={{ background: "#1A1514", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }} />
              <Bar dataKey="plays" fill="#EE964B" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="grid md:grid-cols-[1fr_320px] gap-10">
        <form onSubmit={save} className="space-y-4">
          <h2 className="font-display text-2xl font-black mb-4">Profil</h2>
          <Field label="Nom"><Input value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="dash-name" /></Field>
          <Field label="Photo de profil">
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-full overflow-hidden bg-gradient-to-br from-brand-red to-brand-yellow flex items-center justify-center text-white font-black text-xl shrink-0">
                {form.picture ? <img src={form.picture} alt="" className="w-full h-full object-cover" /> : (form.name?.[0]?.toUpperCase() || "?")}
              </div>
              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => pictureFileRef.current?.click()}
                    disabled={uploadingPic}
                    className="btn-ghost text-xs px-4 py-2 inline-flex items-center gap-2"
                    data-testid="dash-picture-upload-btn"
                  >
                    {uploadingPic ? "Upload..." : "📷 Choisir une photo"}
                  </button>
                  {form.picture && (
                    <button type="button" onClick={() => setForm({ ...form, picture: "" })} className="text-xs text-white/50 hover:text-red-400">Retirer</button>
                  )}
                </div>
                <input
                  ref={pictureFileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className="hidden"
                  onChange={(e) => e.target.files?.[0] && uploadProfilePicture(e.target.files[0])}
                />
                <Input value={form.picture} onChange={(v) => setForm({ ...form, picture: v })} placeholder="ou colle une URL d'image" testid="dash-picture" />
              </div>
            </div>
          </Field>
          <Field label="Bio">
            <textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-brand-red/50 min-h-[100px]" data-testid="dash-bio-input" />
          </Field>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Pays"><Select value={form.country} onChange={(v) => setForm({ ...form, country: v })} items={COUNTRIES} testid="dash-country" /></Field>
            <Field label="Ville / village 🇲🇬"><CityPicker value={form.city} onChange={(v) => setForm({ ...form, city: v })} testid="dash-city" /></Field>
            <Field label="Genre principal"><Select value={form.genre} onChange={(v) => setForm({ ...form, genre: v })} items={GENRES} testid="dash-genre" /></Field>
          </div>

          <h3 className="font-display text-lg font-bold pt-4">Liens externes</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="YouTube"><Input value={form.youtube} onChange={(v) => setForm({ ...form, youtube: v })} placeholder="https://youtube.com/..." testid="dash-youtube" /></Field>
            <Field label="Spotify"><Input value={form.spotify} onChange={(v) => setForm({ ...form, spotify: v })} placeholder="https://spotify.com/..." testid="dash-spotify" /></Field>
            <Field label="Instagram"><Input value={form.instagram} onChange={(v) => setForm({ ...form, instagram: v })} placeholder="https://instagram.com/..." testid="dash-instagram" /></Field>
            <Field label="TikTok"><Input value={form.tiktok} onChange={(v) => setForm({ ...form, tiktok: v })} placeholder="https://tiktok.com/@..." testid="dash-tiktok" /></Field>
          </div>

          <h3 className="font-display text-lg font-bold pt-4">Notifications email</h3>
          <div className="p-4 rounded-xl border border-white/10 bg-white/[0.02]">
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={form.email_opt_in} onChange={(e) => setForm({ ...form, email_opt_in: e.target.checked })} className="accent-brand-red w-4 h-4 mt-0.5" data-testid="dash-email-opt-in" />
              <div>
                <p className="text-sm font-semibold">Recevoir les emails Voamada</p>
                <p className="text-[11px] text-white/50">Nouveau morceau d'un artiste suivi, nouveau follower, récap hebdo</p>
                {!emailEnabled && <p className="text-[11px] text-brand-yellow mt-1">⚠ Le service email sera actif dès que l'admin aura configuré Resend</p>}
              </div>
            </label>
          </div>

          <button type="submit" disabled={saving} className="btn-primary inline-flex items-center gap-2 disabled:opacity-50" data-testid="dash-save-btn">
            <Save size={16} /> {saving ? "Sauvegarde..." : "Enregistrer"}
          </button>
        </form>

        <div>
          <h2 className="font-display text-2xl font-black mb-4">Mes morceaux</h2>
          <div className="space-y-3">
            {tracks.length === 0 && <p className="text-white/60 text-sm">Aucun morceau.</p>}
            {tracks.map((t) => (
              <div key={t.track_id} className="flex items-center gap-3 p-3 rounded-xl border border-white/10 bg-white/[0.02]" data-testid={`dash-track-${t.track_id}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm truncate">{t.title}</p>
                  <p className="text-xs text-white/50">{t.plays} écoutes · {t.likes_count} likes · {t.downloads || 0} DL</p>
                </div>
                <button onClick={() => deleteTrack(t.track_id)} className="text-xs text-red-400 hover:text-red-300" data-testid={`delete-track-${t.track_id}`}>Suppr.</button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, icon, accent }) => (
  <div className={`rounded-xl border p-4 ${accent ? "border-brand-red/30 bg-brand-red/5" : "border-white/10 bg-white/[0.02]"}`}>
    <div className="flex items-center gap-2 mb-2 text-white/50">{icon}<span className="text-xs uppercase tracking-widest">{label}</span></div>
    <p className={`font-display font-black text-3xl ${accent ? "text-brand-red" : "text-white"}`}>{value}</p>
  </div>
);
const Field = ({ label, children }) => (<div><label className="block text-[10px] tracking-[0.25em] uppercase text-white/60 font-bold mb-2">{label}</label>{children}</div>);
const Input = ({ value, onChange, placeholder, testid }) => (<input type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-brand-red/50" data-testid={`${testid}-input`} />);
const Select = ({ value, onChange, items, testid }) => (<select value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3 outline-none" data-testid={testid}>{items.map((i) => <option key={i} value={i} className="bg-brand-dark">{i}</option>)}</select>);

export default DashboardPage;
